import React, { useState, useEffect } from 'react';
import { Book, Server, Wifi, Terminal, AlertCircle, CheckCircle, Copy, Info, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RADIUSOnlyGuide = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [rosVersion, setRosVersion] = useState('ros7');
  const [radiusInfo, setRadiusInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRADIUSInfo();
  }, []);

  const fetchRADIUSInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/system/radius-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRadiusInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching RADIUS info:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Tersalin ke clipboard!');
  };

  const CodeBlock = ({ code, language = 'bash' }) => (
    <div className="relative bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
      <button
        onClick={() => copyToClipboard(code)}
        className="absolute top-2 right-2 p-2 hover:bg-gray-700 rounded transition-colors"
        title="Copy"
      >
        <Copy className="w-4 h-4" />
      </button>
      <pre className="overflow-x-auto text-sm">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'requirements', label: 'Persyaratan', icon: AlertCircle },
    { id: 'pppoe', label: 'PPPoE Setup', icon: Wifi },
    { id: 'hotspot', label: 'Hotspot Setup', icon: Server },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: Terminal }
  ];

  const radiusIp = radiusInfo?.radius_ip || 'YOUR_RADIUS_SERVER_IP';
  const radiusSecret = radiusInfo?.radius_secret || 'YOUR_RADIUS_SECRET';

  return (
    <div className="p-6 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3 dark:text-slate-100">
              <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
              📚 Panduan RADIUS Only Mode
            </h1>
            <p className="text-gray-600 dark:text-slate-300">
              Setup MikroTik dengan RADIUS Server <strong>tanpa VPN</strong> - untuk router dengan IP Publik atau dalam satu network
            </p>
          </div>
          
          {/* RouterOS Version Selector */}
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-2 dark:bg-slate-800 dark:border-slate-700">
            <span className="text-sm font-medium text-gray-700 dark:text-slate-200">RouterOS Version:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setRosVersion('ros6')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  rosVersion === 'ros6' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ROS 6.x
              </button>
              <button
                onClick={() => setRosVersion('ros7')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  rosVersion === 'ros7' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ROS 7.x
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Card className="mb-6 border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/30">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="w-6 h-6 text-green-600 flex-shrink-0 mt-1 dark:text-green-400" />
            <div>
              <h3 className="font-bold text-green-900 mb-2">Mode RADIUS Only - Kapan Menggunakan?</h3>
              <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                <li><strong>Router memiliki IP Publik statis</strong> - RADIUS server bisa diakses langsung</li>
                <li><strong>Router & RADIUS Server dalam satu network/LAN</strong> - tidak perlu VPN</li>
                <li><strong>Sudah setup port forwarding/firewall</strong> - port 1812/1813 accessible</li>
              </ul>
              <p className="mt-3 text-sm text-green-800">
                ⚠️ <strong>Jika router TIDAK punya IP Publik</strong>, gunakan mode <a href="/app/radius-guide" className="underline font-semibold">RADIUS + VPN</a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Book className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  Arsitektur RADIUS Only
                </h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 dark:bg-blue-900/30">
                  <h3 className="font-bold text-blue-900 mb-3">Cara Kerja:</h3>
                  <div className="space-y-2 text-sm text-blue-800">
                    <p>1. <strong>MikroTik Router</strong> → (Direct Connection) → <strong>RADIUS Server</strong></p>
                    <p>2. User login via PPPoE/Hotspot</p>
                    <p>3. MikroTik mengirim authentication request ke RADIUS Server</p>
                    <p>4. RADIUS Server memvalidasi user dari database</p>
                    <p>5. RADIUS mengirim response (Accept/Reject) ke MikroTik</p>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 dark:bg-slate-900 dark:border-slate-700">
                  <h3 className="font-bold text-gray-900 mb-3 dark:text-slate-100">Informasi RADIUS Server Anda:</h3>
                  {loading ? (
                    <p className="text-sm text-gray-600 dark:text-slate-300">Loading...</p>
                  ) : radiusInfo ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">RADIUS IP:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">{radiusIp}</code>
                        <button
                          onClick={() => copyToClipboard(radiusIp)}
                          className="text-gray-500 hover:text-gray-700 p-1 dark:text-slate-400"
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">RADIUS Secret:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">{radiusSecret}</code>
                        <button
                          onClick={() => copyToClipboard(radiusSecret)}
                          className="text-gray-500 hover:text-gray-700 p-1 dark:text-slate-400"
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">Auth Port:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">1812</code>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">Accounting Port:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">1813</code>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 dark:text-red-400">Gagal memuat info RADIUS. Hubungi admin.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Requirements Tab */}
          {activeTab === 'requirements' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                Persyaratan & Checklist
              </h2>

              <Card className="border-orange-200 bg-orange-50 dark:bg-orange-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-orange-900 mb-3">✅ Checklist Sebelum Mulai:</h3>
                  <ul className="space-y-2 text-sm text-orange-800">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>MikroTik Router</strong> dengan RouterOS 6.x atau 7.x</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>IP Publik pada router</strong> ATAU router dalam satu network dengan RADIUS Server</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>Port 1812 & 1813</strong> terbuka di firewall (jika menggunakan internet publik)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>Test koneksi</strong>: Pastikan router bisa ping ke IP RADIUS Server</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>Credentials</strong>: RADIUS IP & Secret (lihat tab Overview)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                <h3 className="font-bold text-gray-900 mb-3 dark:text-slate-100">🧪 Test Koneksi ke RADIUS Server</h3>
                <p className="text-sm text-gray-700 mb-3 dark:text-slate-200">
                  Dari MikroTik Terminal, jalankan perintah berikut untuk test ping:
                </p>
                <CodeBlock code={`/ping ${radiusIp} count=5`} />
                <p className="text-sm text-gray-600 dark:text-slate-300">
                  ✅ Jika berhasil reply, lanjutkan ke setup.<br/>
                  ❌ Jika timeout, periksa firewall/routing/network connectivity.
                </p>
              </div>
            </div>
          )}

          {/* PPPoE Setup Tab */}
          {activeTab === 'pppoe' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Wifi className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                Setup PPPoE dengan RADIUS
              </h2>

              {/* Step 1 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-indigo-600">Step 1</Badge>
                  <h3 className="font-bold text-lg">Tambahkan RADIUS Server</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-3 dark:text-slate-200">
                  Tambahkan RADIUS server ke konfigurasi MikroTik:
                </p>

                {rosVersion === 'ros6' ? (
                  <CodeBlock code={`/radius
add address=${radiusIp} secret=${radiusSecret} service=ppp`} />
                ) : (
                  <CodeBlock code={`/radius
add address=${radiusIp} secret=${radiusSecret} service=ppp`} />
                )}
              </div>

              {/* Step 2 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-indigo-600">Step 2</Badge>
                  <h3 className="font-bold text-lg">Setup PPPoE Server Profile</h3>
                </div>

                <CodeBlock code={`/ppp profile
add name=radius-profile use-encryption=yes local-address=10.10.10.1 remote-address=pppoe-pool dns-server=8.8.8.8,8.8.4.4 use-mpls=no use-compression=no use-vj-compression=no only-one=yes`} />
              </div>

              {/* Step 3 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-indigo-600">Step 3</Badge>
                  <h3 className="font-bold text-lg">Buat IP Pool untuk PPPoE</h3>
                </div>

                <CodeBlock code={`/ip pool
add name=pppoe-pool ranges=10.10.10.2-10.10.10.254`} />
              </div>

              {/* Step 4 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-indigo-600">Step 4</Badge>
                  <h3 className="font-bold text-lg">Enable PPPoE Server</h3>
                </div>

                <CodeBlock code={`/interface pppoe-server server
add service-name=ISP-PPPoE interface=ether2 default-profile=radius-profile authentication=pap,chap,mschap1,mschap2 keepalive-timeout=60`} />

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3 dark:bg-yellow-900/30">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Ganti "ether2"</strong> dengan interface yang terhubung ke client Anda!
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-indigo-600">Step 5</Badge>
                  <h3 className="font-bold text-lg">Enable RADIUS Authentication di PPP</h3>
                </div>

                <CodeBlock code={`/ppp aaa
set use-radius=yes accounting=yes interim-update=5m`} />
              </div>

              {/* Step 6 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-green-600">Step 6</Badge>
                  <h3 className="font-bold text-lg">Verifikasi Setup</h3>
                </div>

                <p className="text-sm text-gray-700 mb-3 dark:text-slate-200">
                  Cek apakah RADIUS server sudah terkonfigurasi:
                </p>
                <CodeBlock code={`/radius print`} />

                <p className="text-sm text-gray-700 mb-3 mt-4 dark:text-slate-200">
                  Cek apakah PPPoE server sudah running:
                </p>
                <CodeBlock code={`/interface pppoe-server server print`} />
              </div>

              <Card className="bg-green-50 border-green-200 dark:bg-green-900/30">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 dark:text-green-400" />
                    <div>
                      <h3 className="font-bold text-green-900 mb-2">✅ Setup PPPoE Selesai!</h3>
                      <p className="text-sm text-green-800">
                        Client sekarang bisa login menggunakan username/password yang terdaftar di RADIUS server.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Hotspot Setup Tab */}
          {activeTab === 'hotspot' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Server className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                Setup Hotspot dengan RADIUS
              </h2>

              {/* Step 1 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-orange-600">Step 1</Badge>
                  <h3 className="font-bold text-lg">Tambahkan RADIUS Server</h3>
                </div>

                <CodeBlock code={`/radius
add address=${radiusIp} secret=${radiusSecret} service=hotspot`} />
              </div>

              {/* Step 2 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-orange-600">Step 2</Badge>
                  <h3 className="font-bold text-lg">Setup Hotspot Server Profile</h3>
                </div>

                <p className="text-sm text-gray-700 mb-3 dark:text-slate-200">
                  Buat atau edit hotspot server profile untuk menggunakan RADIUS:
                </p>
                <CodeBlock code={`/ip hotspot profile
add name=radius-hotspot use-radius=yes`} />
              </div>

              {/* Step 3 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-orange-600">Step 3</Badge>
                  <h3 className="font-bold text-lg">Setup Hotspot Server</h3>
                </div>

                <p className="text-sm text-gray-700 mb-3 dark:text-slate-200">
                  Jika belum ada hotspot server, buat dengan wizard atau manual:
                </p>
                <CodeBlock code={`/ip hotspot
setup

# Atau jika sudah ada, update profile-nya:
/ip hotspot
set [find] profile=radius-hotspot`} />
              </div>

              {/* Step 4 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-orange-600">Step 4</Badge>
                  <h3 className="font-bold text-lg">Enable RADIUS Accounting</h3>
                </div>

                <CodeBlock code={`/ip hotspot profile
set [find name=radius-hotspot] use-radius=yes radius-accounting=yes radius-interim-update=5m`} />
              </div>

              {/* Step 5 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-green-600">Step 5</Badge>
                  <h3 className="font-bold text-lg">Verifikasi Setup</h3>
                </div>

                <CodeBlock code={`/radius print
/ip hotspot profile print`} />
              </div>

              <Card className="bg-green-50 border-green-200 dark:bg-green-900/30">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 dark:text-green-400" />
                    <div>
                      <h3 className="font-bold text-green-900 mb-2">✅ Setup Hotspot Selesai!</h3>
                      <p className="text-sm text-green-800">
                        User sekarang bisa login ke hotspot menggunakan username/password dari RADIUS server.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Troubleshooting Tab */}
          {activeTab === 'troubleshooting' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Terminal className="w-6 h-6 text-red-600 dark:text-red-400" />
                Troubleshooting
              </h2>

              {/* Issue 1 */}
              <Card className="border-red-200">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-red-900 mb-2">❌ User tidak bisa login</h3>
                  <div className="space-y-3 text-sm text-gray-700 dark:text-slate-200">
                    <div>
                      <p className="font-semibold mb-1">Cek 1: RADIUS server reachable?</p>
                      <CodeBlock code={`/ping ${radiusIp} count=5`} />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Cek 2: RADIUS configuration correct?</p>
                      <CodeBlock code={`/radius print detail`} />
                      <p className="text-gray-600 dark:text-slate-300">Pastikan address & secret sudah benar!</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Cek 3: Monitor RADIUS logs</p>
                      <CodeBlock code={`/log print where topics~"radius"`} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issue 2 */}
              <Card className="border-red-200">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-red-900 mb-2">❌ Timeout saat authentication</h3>
                  <div className="space-y-3 text-sm text-gray-700 dark:text-slate-200">
                    <p><strong>Kemungkinan penyebab:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Port 1812/1813 diblok firewall</li>
                      <li>RADIUS server down/tidak running</li>
                      <li>IP RADIUS salah</li>
                    </ul>
                    <div>
                      <p className="font-semibold mb-1">Test port dengan telnet:</p>
                      <CodeBlock code={`/tool telnet ${radiusIp} 1812`} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issue 3 */}
              <Card className="border-red-200">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-red-900 mb-2">❌ RADIUS secret mismatch</h3>
                  <div className="space-y-3 text-sm text-gray-700 dark:text-slate-200">
                    <p>Error: "Wrong RADIUS secret" di log</p>
                    <div>
                      <p className="font-semibold mb-1">Solusi: Update RADIUS secret</p>
                      <CodeBlock code={`/radius set [find] secret=${radiusSecret}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Debug Commands */}
              <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-blue-900 mb-3">🔍 Perintah Debug Berguna</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-semibold text-blue-800 mb-1">Monitor semua log RADIUS:</p>
                      <CodeBlock code={`/log print follow where topics~"radius"`} />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-800 mb-1">Lihat active PPPoE sessions:</p>
                      <CodeBlock code={`/ppp active print`} />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-800 mb-1">Lihat Hotspot active users:</p>
                      <CodeBlock code={`/ip hotspot active print`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RADIUSOnlyGuide;
